import React from 'react';
import styled from 'styled-components';
import { Card, Icon, Tag } from '@repo/design-system';
import { IconDisc } from '@repo/ui';
import type { AuthoredOpeningHoursVersion } from '@repo/frontend-types';
import {
  fontMono,
  semantic,
  space,
  tracking,
  typeRole,
  typeScale,
  weight,
} from '@repo/design-tokens';

import {
  calendarKey,
  calendarListSub,
  pastSummary,
  specialName,
  specialSpan,
  standardName,
  type CalendarGroup,
  type CalendarListGroups,
} from './hoursModel';

/**
 * The Opening hours step's calendars, by the twin's date (spec twin-details-page-hours-week, R7.3;
 * frame 06): *NOW* (the one covering today, marked *Current*), *UPCOMING*, and *PAST* folded behind
 * one card. Each calendar is a design-system glass `Card` — a button whose `aria-current` says
 * which one the week panel shows.
 *
 * Special periods sit in the same groups (spec twin-details-page-hours-special, R4; frame 07): a
 * lavender disc, the name, its dates and a *Special* tag. Each card carries `data-calendar-key`, by
 * which the step returns focus to it when the special period card closes.
 */

export interface CalendarListProps {
  calendars: CalendarListGroups;
  selectedKey: string | null;
  onSelect: (key: string) => void;
  pastOpen: boolean;
  onTogglePast: () => void;
  /** *Add Special Period* above *New Standard Hours From a Date*, at the list's foot (frame 07). */
  foot?: React.ReactNode;
  /**
   * *Edit details* (spec opening-hours-edit-standard-details, R3.1): rendered directly after the
   * selected card, never inside it, since a card is itself a control.
   */
  editSlot?: React.ReactNode;
  className?: string;
}

const CalendarCard: React.FC<{
  version: AuthoredOpeningHoursVersion;
  group: CalendarGroup;
  selected: boolean;
  onSelect: () => void;
}> = ({ version, group, selected, onSelect }) => {
  const special = version.kind === 'special';
  const past = group === 'past';
  return (
    <Card
      surface="on-glass"
      interactive
      selected={selected}
      aria-current={selected ? 'true' : undefined}
      // A special's name is the person's; its tag is part of what the card is (R4.2).
      aria-label={special ? `${specialName(version)}, ${specialSpan(version)}, Special` : undefined}
      data-calendar-key={calendarKey(version)}
      className="calendar"
      onClick={onSelect}
    >
      {/* No `flat`: these tones carry no glow, and their glow slot is the 1 px white ring (R11). */}
      <IconDisc tone={past ? 'neutral' : special ? 'lavender' : 'aqua'}>
        <Icon name={past ? 'box-archive' : special ? 'calendar-days' : 'clock'} size="sm" />
      </IconDisc>
      <span className="text">
        <span className="title">{special ? specialName(version) : standardName(version)}</span>
        <span className="sub">
          {special ? specialSpan(version) : calendarListSub(version, group)}
        </span>
      </span>
      {special ? (
        <Tag surface="on-glass" size="dense" tone="lavender">
          Special
        </Tag>
      ) : (
        group === 'now' && (
          <Tag surface="on-glass" size="dense" tone="mint">
            Current
          </Tag>
        )
      )}
    </Card>
  );
};

const CalendarList: React.FC<CalendarListProps> = ({
  calendars,
  selectedKey,
  onSelect,
  pastOpen,
  onTogglePast,
  foot,
  editSlot,
  className,
}) => {
  const { now, upcoming, past } = calendars;
  const summary = pastSummary(past);
  return (
    <nav className={className} aria-label="Opening hours calendars">
      <div className="groups">
        <section aria-labelledby="oh-group-now">
          <h3 className="caption" id="oh-group-now">
            Now
          </h3>
          {!now.some(v => v.kind === 'standard') && <p className="none">No standard hours today</p>}
          {now.map(v => (
            <React.Fragment key={calendarKey(v)}>
              <CalendarCard
                version={v}
                group="now"
                selected={selectedKey === calendarKey(v)}
                onSelect={() => onSelect(calendarKey(v))}
              />
              {selectedKey === calendarKey(v) && editSlot}
            </React.Fragment>
          ))}
        </section>
        {upcoming.length > 0 && (
          <section aria-labelledby="oh-group-upcoming">
            <h3 className="caption" id="oh-group-upcoming">
              Upcoming
            </h3>
            {upcoming.map(v => (
              <React.Fragment key={calendarKey(v)}>
                <CalendarCard
                  version={v}
                  group="upcoming"
                  selected={selectedKey === calendarKey(v)}
                  onSelect={() => onSelect(calendarKey(v))}
                />
                {selectedKey === calendarKey(v) && editSlot}
              </React.Fragment>
            ))}
          </section>
        )}
        {summary && (
          <section aria-labelledby="oh-group-past">
            <h3 className="caption" id="oh-group-past">
              Past
            </h3>
            <Card
              surface="on-glass"
              interactive
              className="calendar"
              aria-expanded={pastOpen}
              onClick={onTogglePast}
            >
              <IconDisc tone="neutral">
                <Icon name="box-archive" size="sm" />
              </IconDisc>
              <span className="text">
                <span className="title">{summary.title}</span>
                <span className="sub">{summary.sub}</span>
              </span>
              <Icon name={pastOpen ? 'chevron-up' : 'chevron-down'} size="xs" />
            </Card>
            {pastOpen &&
              past.map(v => (
                <CalendarCard
                  key={calendarKey(v)}
                  version={v}
                  group="past"
                  selected={selectedKey === calendarKey(v)}
                  onSelect={() => onSelect(calendarKey(v))}
                />
              ))}
          </section>
        )}
      </div>
      {foot && <div className="foot">{foot}</div>}
    </nav>
  );
};

export default styled(CalendarList)`
  /* Fills the column, so the foot's actions sit at its bottom as frame 06 draws them. */
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${space[4]};
  min-width: 0;

  .groups {
    display: flex;
    flex-direction: column;
    gap: ${space[4]};
  }

  section {
    display: flex;
    flex-direction: column;
    gap: ${space[2]};
  }

  /*
   * Frame 06's group captions: mono, caps, 10/14, tracked — from tokens. The frame draws them in
   * ink-4, under the 4.5:1 text bar at this size; ink-3 (semantic.text.body) clears it. A recorded
   * difference for Rob.
   */
  .caption {
    margin: 0;
    font-family: ${fontMono};
    font-size: ${typeScale.caption2.fontSize};
    line-height: ${typeScale.caption2.lineHeight};
    font-weight: ${weight.medium};
    letter-spacing: ${tracking.label};
    text-transform: uppercase;
    color: ${semantic.text.body};
  }

  /* A calendar is one row (frame 06): disc, the two lines, then the tag — Card stacks by default. */
  .calendar {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${space[3]};
    text-align: left;
  }

  .text {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  .title {
    ${typeRole.body1Strong};
    color: ${semantic.text.primary};
  }

  .sub {
    ${typeRole.caption1};
    color: ${semantic.text.secondary};
  }

  .none {
    margin: 0;
    ${typeRole.caption1};
    color: ${semantic.text.secondary};
  }

  .foot {
    display: flex;
    flex-direction: column;
    gap: ${space[2]};
    margin-top: auto;
  }
`;
